import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authErrorMessage, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function Login() {
  const { user, login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";

  useEffect(() => {
    if (user) navigate(redirect, { replace: true });
  }, [user, navigate, redirect]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(redirect);
    } catch (err) {
      toast.error(err?.message?.replace("Firebase: ", "") || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    try {
      const signedInUser = await loginWithGoogle();
      if (signedInUser) {
        toast.success("Signed in with Google");
        navigate(redirect);
      }
    } catch (error) {
      console.error(error);
      toast.error(authErrorMessage(error));
    }
  };

  return (
    <div data-testid="login-page" className="container-nc py-20 min-h-[70vh] grid place-items-center">
      <div className="w-full max-w-md">
        <p className="overline mb-4 text-center">Welcome back</p>
        <h1 className="font-serif text-4xl md:text-5xl text-espresso text-center leading-tight">
          Log in to <span className="italic">Note Console</span>
        </h1>
        <p className="text-muted_brown text-center mt-3 text-sm">
          Continue your planning journey.
        </p>
        {redirect === "/checkout" && (
          <p className="text-xs text-muted_brown text-center mt-2">
            After login we'll return you to checkout — your details are already saved.
          </p>
        )}

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <input required type="email" placeholder="Email" data-testid="login-email" className="input-nc" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input required type="password" placeholder="Password" data-testid="login-password" className="input-nc" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button data-testid="login-submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
            {loading ? "Signing in..." : "Log In"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted_brown">
          <div className="flex-1 h-px bg-line"/> or <div className="flex-1 h-px bg-line"/>
        </div>

        <button onClick={google} data-testid="login-google" className="btn-outline w-full">
          Continue with Google
        </button>

        <p className="text-sm text-muted_brown text-center mt-8">
          New to Note Console? <Link to={`/signup${redirect !== "/dashboard" ? `?redirect=${redirect}` : ""}`} className="text-espresso underline">Create an account</Link>
        </p>
        <div className="mt-5 flex justify-center">
          <Link
            to="/admin/login"
            data-testid="login-admin-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-muted_brown hover:text-espresso border border-line rounded-full px-4 py-2 bg-ivory/70"
          >
            <Lock size={12} strokeWidth={1.6} /> Admin login
          </Link>
        </div>
      </div>
    </div>
  );
}
